/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { task } from 'ember-concurrency';
import { Dropdown as BasicDropdown } from 'ember-basic-dropdown/components/basic-dropdown';
import { queryEncode } from 'cio/helpers/query';
import { isEmptyFilters } from 'cio/utils/filters';
import CustomerService, { OtherExportType } from 'cio/services/customer';
import SettingsService from 'cio/services/settings';
import EnvironmentService from 'cio/services/environment';
import { SerializedLegacyFilter } from 'cio/types/conditions/legacy-filter';
import { Filters } from 'cio/utils/filters';
import { tracked } from '@glimmer/tracking';
import ObjectTypeModel from 'cio/models/object-type';
import ObjectTypesService from 'cio/services/object-types';
import { IdentifierKey, ObjectIdentifierKey } from 'cio/utils/identifiers';
import { isEmpty } from '@ember/utils';

enum ExportType {
  AllAttributes = 'all_attributes',
  DisplayedAttributes = 'displayed_attributes',
  ChooseAttributes = 'choose_attributes',
}

interface Signature {
  Element: HTMLDivElement;
  Args: {
    isDisabled: boolean;
    emailCondition?: string | null;
    filterConditions: SerializedLegacyFilter[] | Filters;
    objectType?: ObjectTypeModel;
  };
}

export const HIDDEN_ATTRIBUTES = ['_last_emailed', '_last_visit', '_cio_subscription_preferences_computed'];

export const PERMANENT_PEOPLE_ATTRIBUTES = [IdentifierKey.Id, IdentifierKey.Email, 'created_at', 'unsubscribed'];
export const PERMANENT_OBJECT_ATTRIBUTES = [ObjectIdentifierKey.ObjectId, 'name', 'created_at'];

export default class CioExportColumns extends Component<Signature> {
  @service customer!: CustomerService;
  @service settings!: SettingsService;
  @service environment!: EnvironmentService;
  @service objectTypes!: ObjectTypesService;

  readonly exportType = ExportType;

  @tracked selectedAttributes = this.args.objectType != null ? ['object_id'] : ['id'];
  @tracked requiredAttributes = this.args.objectType != null ? ['object_id'] : ['id'];
  @tracked prependAttributes =
    this.args.objectType != null
      ? [...PERMANENT_OBJECT_ATTRIBUTES, ...(this.args.objectType?.sentAttributes ?? [])]
      : [...PERMANENT_PEOPLE_ATTRIBUTES, ...HIDDEN_ATTRIBUTES];
  @tracked isChoosingAttributes = false;
  @tracked isShowingExportProgress = false;
  @tracked isShowingExportOther = false;
  @tracked lastExportTaskSuccess: boolean | null = null;
  @tracked lastError: string | null = null;
  @tracked lastExportAction?: () => unknown;
  @tracked showExportRelationshipsModal = false;
  @tracked selectedExportType: ExportType | null = null;
  @tracked includeRelationships = false;
  @tracked selectedExportAttributes: string[] | null = null;
  @tracked lastExportedAttributes: string[] = [];

  get hasObjects() {
    if (this.args.objectType == null) {
      // args.objectType will be null if export is started from people list page
      return this.objectTypes.objectCount > 0;
    }

    return this.args.objectType.objectCount > 0;
  }

  get lastExportedAttributesKey() {
    let objectTypeId = this.args.objectType?.id;
    if (objectTypeId) {
      return `lastExportedAttributes::o${objectTypeId}`;
    }
    return 'lastExportedAttributes::people';
  }

  #getLastExportedAttributes() {
    try {
      let attrs = localStorage.getItem(this.lastExportedAttributesKey);
      if (!isEmpty(attrs)) {
        this.lastExportedAttributes = JSON.parse(attrs as string) as string[];
        return;
      }
      this.lastExportedAttributes = [];
    } catch (err) {
      this.lastExportedAttributes = [];
    }
  }

  #saveLastExportedAttributes(attributes: string[] | null) {
    if (attributes != null) {
      try {
        localStorage.setItem(this.lastExportedAttributesKey, JSON.stringify(attributes));
        this.lastExportedAttributes = attributes;
      } catch (err) {}
    }
  }

  exportAttributes = task({ drop: true }, async (attributes: string[] | null) => {
    let { emailCondition, filterConditions } = this.args;

    this.isChoosingAttributes = false;
    this.isShowingExportProgress = true;
    this.lastExportTaskSuccess = null;
    this.lastExportAction = () => this.exportAttributesAction(attributes);

    try {
      if (this.args.objectType != null) {
        await this.objectTypes.exports({
          typeId: this.args.objectType.id,
          filters: isEmptyFilters(filterConditions) ? null : queryEncode(filterConditions),
          attributes,
          from: 'object_list',
          includeRelationships: this.includeRelationships,
        });
      } else {
        await this.customer.exports({
          email: emailCondition,
          filters: isEmptyFilters(filterConditions) ? null : queryEncode(filterConditions),
          attributes,
          from: 'people_list',
          includeRelationships: this.includeRelationships,
        });
      }
      this.#saveLastExportedAttributes(attributes);
      this.lastExportTaskSuccess = true;
    } catch (error) {
      this.lastExportTaskSuccess = false;
      this.lastError = error;
    } finally {
      this.includeRelationships = false;
    }
  });

  @action
  exportAttributesAction(attributes: string[] | null) {
    this.selectedExportAttributes = attributes;
    this.isChoosingAttributes = false;

    if (this.hasObjects) {
      this.showExportRelationshipsModal = true;
    } else {
      this.exportAttributes.unlinked().perform(attributes);
    }
  }

  exportOther = task(
    { drop: true },
    async (exportType: OtherExportType, attributes: string[], duplicatedAttribute: string | null) => {
      this.isShowingExportOther = false;
      this.isShowingExportProgress = true;
      this.lastExportTaskSuccess = null;
      this.lastExportAction = () => this.exportOtherAction(exportType, attributes, duplicatedAttribute);

      try {
        await this.customer.exportOther({ exportType, attributes, duplicatedAttribute });
        this.lastExportTaskSuccess = true;
      } catch (error) {
        this.lastExportTaskSuccess = false;
        this.lastError = error;
      }
    },
  );

  @action
  exportOtherAction(exportType: OtherExportType, attributes: string[], duplicatedAttribute: string | null) {
    return this.exportOther.perform(exportType, attributes, duplicatedAttribute);
  }

  @action
  displayExportOther(dropdown: BasicDropdown) {
    dropdown.actions.close();
    this.isShowingExportOther = true;
  }

  @action
  startExport(dropdown: BasicDropdown, exportType: ExportType) {
    this.selectedExportType = exportType;
    dropdown.actions.close();
    this.#getLastExportedAttributes();

    if (exportType === ExportType.ChooseAttributes) {
      this.isChoosingAttributes = true;
      return;
    }

    if (this.hasObjects) {
      this.showExportRelationshipsModal = true;
    } else {
      this.exportToCsv();
    }
  }

  @action
  exportToCsv() {
    switch (this.selectedExportType) {
      case ExportType.AllAttributes:
        this.#exportAllAttributes();
        break;
      case ExportType.DisplayedAttributes:
        this.#exportDisplayedAttributes();
        break;
      case ExportType.ChooseAttributes:
        this.#exportChosenAttributes();
        break;
    }
    this.showExportRelationshipsModal = false;
  }

  #exportDisplayedAttributes() {
    let columns: string[];

    if (this.args.objectType) {
      let displayedColumns = this.settings.environmentSettings.objectTypeColumns[this.args.objectType.id] ?? [];
      columns = [...displayedColumns, ...PERMANENT_OBJECT_ATTRIBUTES].uniq();
    } else {
      columns = [...this.settings.environmentSettings.customerColumns.slice(), ...PERMANENT_PEOPLE_ATTRIBUTES].uniq();
    }

    return this.exportAttributes.perform(columns);
  }

  #exportAllAttributes() {
    this.exportAttributes.perform(null);
  }

  #exportChosenAttributes() {
    this.exportAttributes.unlinked().perform(this.selectedExportAttributes);
  }
}
